import { useEffect } from 'react';

import { Money, formatMoney } from '@betterment/core';
import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';
import { StepProps, useFlowState } from '@betterment/js-runtime';
import {
  Banner,
  Box,
  Button,
  CenteredLayout,
  Dialog,
  Heading,
  Icon,
  IconButton,
  PancakeStack,
  Separator,
  Text,
  WorkflowHeader,
} from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { ErrorBoundary } from '@betterment/shared-components';
import {
  moneyFromSharesTradeEstimate,
  moneyTradeEstimateCopy,
  sharesFromMoneyTradeEstimate,
  sharesTradeEstimateCopy,
} from '@betterment/shared-components/src/utils/tradeEstimateHelpers';
import { Form, Formik, FormikProps } from 'formik';
import { useNavigate } from 'react-router-dom';

import { OrderUnitType, SaleScope, SellOrderProps, ValidatedOrderValues, Validations } from './types';

import { CreateFinancialSecurityOrderRequestDocument } from '../../buy-order-flow/graphql/mutation.createFinancialSecurityOrderRequest.generated';
import { EstimatedTradeTimingDialog } from '../../buy-order-flow/steps/EstimatedTradeTimingDialog';
import { FinancialSecurityOrderRequestDirection, TaxImpact as GraphQLTaxImpact } from '../../graphql/schema';
import { WithLayout } from '../../RetailApp/components/WithLayout';
import { TaxImpact } from '../../Shared/data';
import { locationAssign } from '../../Shared/path-helpers';
import { TaxImpactDialogContent, formatEstimatedTaxes } from '../../Shared/TaxImpact';
import { DirectTradingAccountSellSecurityPositionsDocument } from '../graphql/directTradingAccountSellSecurityPositions.generated';

export type ReviewStepProps = SellOrderProps & StepProps;

const convertGraphQLTaxImpactToTaxImpact = (graphqlTaxImpact: GraphQLTaxImpact): TaxImpact => {
  return {
    transactionPending: graphqlTaxImpact.hasTransactionPending,
    longTermGainCents: graphqlTaxImpact.longTermGain.cents,
    longTermLossCents: graphqlTaxImpact.longTermLoss.cents,
    shortTermGainCents: graphqlTaxImpact.shortTermGain.cents,
    shortTermLossCents: graphqlTaxImpact.shortTermLoss.cents,
    longTermWashedCents: graphqlTaxImpact.longTermWashed.cents,
    longTermPermanentlyWashedCents: graphqlTaxImpact.longTermPermanentlyWashed.cents,
    shortTermWashedCents: graphqlTaxImpact.shortTermWashed.cents,
    shortTermPermanentlyWashedCents: graphqlTaxImpact.shortTermPermanentlyWashed.cents,
    netLongTermCents: graphqlTaxImpact.netLongTerm.cents,
    netLongTermBeforeWashCents: graphqlTaxImpact.netLongTermBeforeWash.cents,
    netShortTermCents: graphqlTaxImpact.netShortTerm.cents,
    netShortTermBeforeWashCents: graphqlTaxImpact.netShortTermBeforeWash.cents,
    netGainCents: graphqlTaxImpact.netGain.cents,
    netWashedLossesCents: graphqlTaxImpact.netWashedLosses.cents,
    netEstimatedTaxesCents: graphqlTaxImpact.netEstimatedTaxes.cents,
    lossWithWashed: graphqlTaxImpact.isLossWithWashed,
    gain: graphqlTaxImpact.isGain,
    howWeEstimateTaxImpactCopy: graphqlTaxImpact.copy.howWeEstimateTaxImpact,
    longTermGainEstimationCopy: graphqlTaxImpact.copy.longTermGainEstimation,
    pendingCopy: graphqlTaxImpact.copy.pendingExplanation,
    washCopy: graphqlTaxImpact.copy.washSaleExplanation,
    shortTermGainEstimationCopy: graphqlTaxImpact.copy.shortTermGainEstimation,
    taxMinExplanationCopy: graphqlTaxImpact.copy.taxMinExplanation,
    hasNonCoveredTaxLots: graphqlTaxImpact.hasNonCoveredTaxLots,
    mayImpactGainsAllowance: graphqlTaxImpact.mayImpactGainsAllowance,
    estimatedTaxImpactBannerCopy: graphqlTaxImpact.copy.estimatedTaxImpactBanner,
    estimatedTaxImpactDisclosureCopy: graphqlTaxImpact.copy.estimatedTaxImpactDisclosure,
  };
};

const ShortTermGainsBanner: React.FC = () => (
  <Banner variant="info">
    <Banner.Content>
      <Text>At the estimated price, this sale will result in short-term capital gains.</Text>
      <Dialog>
        <Dialog.Trigger asChild={true}>
          <Button type="button" variant="textPrimary" aria-label="Learn more">
            Learn more
          </Button>
        </Dialog.Trigger>
        <Dialog.Content css={{ p: 'space.400', width: '50%' }}>
          <Dialog.Title>
            <Heading>How to avoid short-term capital gains</Heading>
          </Dialog.Title>
          <Dialog.Description>
            Selling now means paying a higher tax rate on your gains. If you hold a security for over a year from when
            you bought it, you’ll qualify for lower long-term capital gains rates and keep more of your money.
          </Dialog.Description>
          <Dialog.Close asChild={true}>
            <Button variant="solidSecondary" css={{ mt: 'space.300' }}>
              Close
            </Button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog>
    </Banner.Content>
  </Banner>
);

export const ReviewStep: React.FC<ReviewStepProps> = ({ accountId, securityId, goToNextStep, goToStep }) => {
  const { data } = useGraphQLQuery(DirectTradingAccountSellSecurityPositionsDocument, {
    variables: { accountId },
    throwOnError: true,
  });

  const { flowState } = useFlowState<ValidatedOrderValues & Validations>();
  const navigate = useNavigate();

  const [createFinancialSecurityOrderRequest, { loading: isMutationLoading }] = useGraphQLMutation(
    CreateFinancialSecurityOrderRequestDocument
  );

  const inconsistentUnitAndValues = (values: ValidatedOrderValues) => {
    if (!values.order_unit) {
      return true;
    }

    if (values.order_unit === OrderUnitType.Dollars && !values.order_dollar_amount_cents) {
      return true;
    }

    if (values.order_unit === OrderUnitType.Shares && !values.order_shares_amount) {
      return true;
    }

    return false;
  };

  const estimatedTradeTiming = flowState.values.estimated_trade_timing;
  const taxImpactPreview = flowState.values.tax_impact_preview;

  useEffect(() => {
    if (inconsistentUnitAndValues(flowState.values)) {
      goToStep('form');
    }
  }, [flowState.values, goToStep]);

  if (!data) return null;

  const { account } = data;
  const securityPosition = account.securityPositions.find(sp => sp.financialSecurity.id === securityId);

  if (!securityPosition) return null;

  const { financialSecurity } = securityPosition;
  const price = financialSecurity.statistics.latestPrice.amount;

  const taxImpact = taxImpactPreview ? convertGraphQLTaxImpactToTaxImpact(taxImpactPreview) : null;

  const handleSubmit = async (values: ValidatedOrderValues) => {
    if (inconsistentUnitAndValues(values)) {
      return;
    }

    const value = () => {
      if (values.order_dollar_amount_cents) {
        return { amount: Money(values.order_dollar_amount_cents) };
      }
      return { shares: Number(values.order_shares_amount) };
    };

    try {
      await createFinancialSecurityOrderRequest({
        variables: {
          input: {
            accountId: { directTradingAccountId: data.account.id },
            financialSecurityId: financialSecurity.id,
            direction: FinancialSecurityOrderRequestDirection.Sell,
            value: value(),
            sellAll: values.order_type === SaleScope.SellAll,
            estimatedShares: sharesFromMoneyTradeEstimate(
              values.order_dollar_amount_cents ? Money(values.order_dollar_amount_cents) : undefined,
              price
            ),
            estimatedMoneyAmount: moneyFromSharesTradeEstimate(values.order_shares_amount, price),
            sharePriceAtRequest: price,
          },
        },
      });

      goToNextStep();
    } catch (error) {
      void navigate('/generic_error');
    }
  };

  const formattedAmountCopy = ({ values }: FormikProps<ValidatedOrderValues>) => {
    if (values.order_unit === OrderUnitType.Dollars) {
      return values.order_dollar_amount_cents && formatMoney(Money(values.order_dollar_amount_cents));
    }
    const shareString = `${values.order_shares_amount} share${values.order_shares_amount === 1 ? '' : 's'}`;
    if (values.order_type === SaleScope.SellAll) {
      return `All ${shareString}`;
    }
    return shareString;
  };

  const estimatedCopy = ({ values }: FormikProps<ValidatedOrderValues>) => {
    if (values.order_unit === OrderUnitType.Dollars) {
      return sharesTradeEstimateCopy(
        values.order_dollar_amount_cents ? Money(values.order_dollar_amount_cents) : undefined,
        price
      );
    }

    return moneyTradeEstimateCopy(values.order_shares_amount, price);
  };

  const initialValues: ValidatedOrderValues = {
    order_dollar_amount_cents: flowState.values.order_dollar_amount_cents,
    order_shares_amount: flowState.values.order_shares_amount,
    order_unit: flowState.values.order_unit,
    order_type: flowState.values.order_type,
  };

  return (
    <ErrorBoundary>
      <WithLayout parts={{ sidebar: false, footer: true }}>
        <PancakeStack css={{ maxWidth: '100%', bg: 'background.surface' }}>
          <PancakeStack.Header
            css={{
              maxWidth: '100%',
              display: 'flex',
              justifyContent: 'center',
              borderBottomStyle: 'solid',
              borderBottomWidth: 'thin',
              borderColor: 'border',
            }}
          >
            <WorkflowHeader css={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
              <WorkflowHeader.Leading>
                <IconButton
                  name="LeftArrowSmall"
                  label="Back"
                  spacing="compact"
                  onClick={() =>
                    goToStep('form', {
                      order_dollar_amount_cents: flowState.values.order_dollar_amount_cents,
                      order_shares_amount: flowState.values.order_shares_amount,
                      order_unit: flowState.values.order_unit,
                      order_type: flowState.values.order_type,
                    })
                  }
                />
              </WorkflowHeader.Leading>
              <WorkflowHeader.Title />
              <WorkflowHeader.Trailing>
                <IconButton
                  name="X"
                  label="Close"
                  spacing="compact"
                  onClick={() => locationAssign(flowState.returnToPath)}
                />
              </WorkflowHeader.Trailing>
            </WorkflowHeader>
          </PancakeStack.Header>
          <PancakeStack.Main css={{ paddingRight: 'space.200', paddingLeft: 'space.200' }}>
            <CenteredLayout size="medium" css={{ paddingTop: 'space.500' }}>
              <Heading css={{ marginBottom: 'space.200' }}>Review {financialSecurity.symbol} sell</Heading>
              <Text css={{ marginBottom: 'space.300' }}>Submitted orders can't be canceled or edited.</Text>
              <Formik onSubmit={handleSubmit} initialValues={initialValues}>
                {(formikProps: FormikProps<ValidatedOrderValues>) => (
                  <Form className={css({ width: '100%' })}>
                    <Box
                      css={{
                        width: '100%',
                        borderRadius: 'medium',
                        border: 'token(borderWidths.thin) solid token(colors.border)',
                        padding: 'space.300',
                      }}
                    >
                      <Box
                        css={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box css={{ alignSelf: 'center' }}>
                          <Text>Security</Text>
                        </Box>
                        <Box css={{ display: 'flex', flexDirection: 'column', textAlign: 'end' }}>
                          <Text css={{ alignSelf: 'end' }}>{financialSecurity.name}</Text>
                          <Text css={{ alignSelf: 'end', color: 'text.weak' }} variant="text50">
                            {financialSecurity.symbol}
                          </Text>
                        </Box>
                      </Box>
                      <Separator
                        css={{
                          marginY: 'space.300',
                        }}
                      />
                      <Box
                        css={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box css={{ alignSelf: 'center' }}>
                          <Text>Amount</Text>
                        </Box>
                        <Box css={{ display: 'flex', flexDirection: 'column', textAlign: 'end' }}>
                          <Text css={{ alignSelf: 'end' }}>{formattedAmountCopy(formikProps)}</Text>
                          <Text css={{ alignSelf: 'end', color: 'text.weak' }} variant="text50">
                            {estimatedCopy(formikProps)}
                          </Text>
                        </Box>
                      </Box>
                      {taxImpact && (
                        <>
                          <Separator
                            css={{
                              marginY: 'space.300',
                            }}
                          />
                          <Dialog>
                            <Dialog.Trigger asChild={true}>
                              <Box
                                css={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  cursor: 'pointer',
                                }}
                              >
                                <Box css={{ alignSelf: 'center' }}>
                                  <Text>Estimated taxes {taxImpact.netEstimatedTaxesCents < 0 ? 'saved' : 'owed'}</Text>
                                </Box>
                                <Box
                                  css={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    textAlign: 'right',
                                    gap: 'space.100',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Text css={{ alignSelf: 'end' }}>{formatEstimatedTaxes(taxImpact)}</Text>
                                  <Icon size="small" name="CaretRight" />
                                </Box>
                              </Box>
                            </Dialog.Trigger>
                            <TaxImpactDialogContent {...taxImpact} />
                          </Dialog>
                        </>
                      )}
                      {estimatedTradeTiming && (
                        <>
                          <Separator
                            css={{
                              marginY: 'space.300',
                            }}
                          />
                          <EstimatedTradeTimingDialog estimatedTradeTiming={estimatedTradeTiming} />
                        </>
                      )}
                      {taxImpact && taxImpact.netShortTermCents > 0 && (
                        <>
                          <Separator
                            css={{
                              marginY: 'space.300',
                            }}
                          />
                          <ShortTermGainsBanner />
                        </>
                      )}
                    </Box>
                    <Button
                      css={{
                        marginTop: 'space.300',
                        px: 'space.600',
                      }}
                      type="submit"
                      variant="solidPrimary"
                      loading={isMutationLoading}
                    >
                      Submit
                    </Button>
                  </Form>
                )}
              </Formik>
            </CenteredLayout>
          </PancakeStack.Main>
        </PancakeStack>
      </WithLayout>
    </ErrorBoundary>
  );
};
